/**
 *  @description  Manages all the cameras in the system. All created cameras are given an gUID
 *  that should be used to ID each camera.
 */

import PubSub from 'pubsub-js';
import * as THREE from 'three';

import { CameraTransformation } from '../../../typings/api/skymap/rest/v0/.common';
import { FlyCameraController } from './cameras/controllers/fly-controls';
import { FpsCameraController } from './cameras/controllers/fps-control';
import { OrbitCameraController } from './cameras/controllers/orbit-controls';
import { OrthotileCameraController } from './cameras/controllers/orthotile-controls';
import { SkyviewFpsCamera } from './cameras/skyview-fps-camera';
import { SkyviewOrthographicCamera } from './cameras/skyview-orthographic-camera';
import { SkyviewPerspectiveCamera } from './cameras/skyview-perspective-camera';
import { CameraControllerInterface, CameraType, SkyviewCamera } from './cameras/types';
import { CoordinateManager } from './coordinate-manager';
import { publish, SubscriptionTopic } from './messaging/pubsub';
import { CameraControl } from './view-settings';

const perspectiveControllers: Record<
  CameraControl,
  new (camera: SkyviewPerspectiveCamera) => OrbitCameraController
> = {
  orbit: OrbitCameraController,
  fly: FlyCameraController,
};

class CameraManager {
  private activeCamera: SkyviewCamera | undefined;
  private cameras: { [key: string]: SkyviewCamera };
  private availableCameras: string[];

  private wgs84Coords: THREE.Vector2 | undefined;

  private canvasWidth: number;
  private canvasHeight: number;

  private activeController: CameraControllerInterface | undefined;
  /** How the perspective camera is controlled, while it is. */
  private cameraControl: CameraControl | undefined;

  private hasFocusedOnScene: boolean;
  /** The subscription to the projection's updates, see init. */
  private projectionUpdatedToken: string | undefined;

  // TODO: Refactor how camera transformations are handled
  private sharedViewCameraTransformation?: CameraTransformation;
  private blockFirstPositionalCorrection = false;

  constructor() {
    this.cameras = {};

    this.canvasWidth = 0;
    this.canvasHeight = 0;

    this.hasFocusedOnScene = false;

    this.availableCameras = [];
  }

  /**
   *  @description Initializes the different cameras in the scene
   */
  init() {
    // Once, also when SkyView opens again
    if (this.projectionUpdatedToken) {
      PubSub.unsubscribe(this.projectionUpdatedToken);
    }
    this.projectionUpdatedToken = PubSub.subscribe(
      CoordinateManager.getInstance().projectionUpdatedEvent,
      this.updateCameraLocalPosition.bind(instance),
    );

    if (this.cameras !== undefined) {
      Object.keys(this.cameras).map((key) => {
        delete this.cameras[key];
      });
    }
    this.cameras = {};
  }

  /**
   * @description Clear the camera manager
   */
  clearCustom() {
    if (this.projectionUpdatedToken) {
      PubSub.unsubscribe(this.projectionUpdatedToken);
      this.projectionUpdatedToken = undefined;
    }

    if (this.cameras !== undefined) {
      Object.keys(this.cameras).map((key) => {
        delete this.cameras[key];
      });
    }
    this.cameras = {};
    this.activeCamera = undefined;
    this.wgs84Coords = undefined;
    this.hasFocusedOnScene = false;
  }

  /**
   * @param fov
   * @param aspect
   * @param near
   * @param far
   * @description Creates a perspective camera binds it to an gUID
   */
  createPerspectiveCamera(fov: number, aspect: number, near: number, far: number): string {
    const camera = new SkyviewPerspectiveCamera(
      fov,
      this.canvasWidth / this.canvasHeight,
      near,
      far,
    );
    /** TODO fix! */
    camera.move(new THREE.Vector3(0, 0, 0));
    camera.zooming(150);
    camera.up.set(0, 1, 0);

    this.cameras[camera.uuid] = camera;

    this.activeCamera = this.activeCamera || camera;

    this.activeController =
      this.activeController ||
      new OrbitCameraController(this.activeCamera as SkyviewPerspectiveCamera);

    return camera.uuid;
  }

  /**
   * @param fov
   * @param aspect
   * @param near
   * @param far
   * @description Creates a perspective camera binds it to an gUID
   */
  createFPSCamera(fov: number, aspect: number, near: number, far: number): string {
    const camera = new SkyviewFpsCamera(fov, this.canvasWidth / this.canvasHeight, near, far);

    camera.move(new THREE.Vector3(0, 0, 0));
    camera.zooming(150);

    this.cameras[camera.uuid] = camera;

    this.activeCamera = this.activeCamera || camera;

    this.activeController =
      this.activeController || new FpsCameraController(this.activeCamera as SkyviewFpsCamera);

    return camera.uuid;
  }

  /**
   * @param width
   * @param height
   * @param near
   * @param far
   * @description Creates a orthographic camera binds it to an gUID
   */
  createOrthographicCamera(width: number, height: number, near: number, far: number): string {
    const camera = new SkyviewOrthographicCamera(width, height, near, far);

    // Increased to allow objects above 200 MAS to be displayed
    // 5000 because largest average MAS in the world by country is 3,280 (Bhutan)
    camera.position.z = 5000;
    camera.up.set(0, 1, 0);
    camera.lookAt(new THREE.Vector3(0, 0, 0));

    this.cameras[camera.uuid] = camera;

    this.activeCamera = this.activeCamera || camera;

    this.activeController =
      this.activeController ||
      new OrthotileCameraController(this.activeCamera as SkyviewOrthographicCamera);

    return camera.uuid;
  }

  /**
   *  Set shared view camera transformation.
   *  Used to override the first scene focus
   * @param cameraTransformation
   */
  setCameraTransformation(cameraTransformation?: CameraTransformation) {
    this.sharedViewCameraTransformation = cameraTransformation;
  }

  /** Moves the shared view camera transformation, if any, before the scene is focused. */
  transformSharedViewCamera(
    transform: (cameraTransformation: CameraTransformation) => CameraTransformation,
  ) {
    if (this.sharedViewCameraTransformation) {
      this.sharedViewCameraTransformation = transform(this.sharedViewCameraTransformation);
    }
  }

  /**
   *
   */
  focusOnScene(): void {
    // Very hacky way to ensure that the camera is not missplaced when
    // focused according to the shared view transformation
    // TODO: Refactor the camera positioning and transformation
    // TODO: Not enough time cuz deployment, but this will have to be cleaned up
    if (this.sharedViewCameraTransformation) {
      this.activeCamera!.applyTransformation(this.sharedViewCameraTransformation);
      this.sharedViewCameraTransformation = undefined;
      this.blockFirstPositionalCorrection = true;
      this.hasFocusedOnScene = true;
    } else if (!this.hasFocusedOnScene) {
      this.activeController!.focusOnScene();
      this.hasFocusedOnScene = true;
    }
  }

  /**
   * @description Update the cameras local position based on the last registered wgs84 coordinates.
   */
  updateCameraLocalPosition() {
    if (
      !this.activeCamera ||
      !this.wgs84Coords ||
      !CoordinateManager.getInstance().hasCoordinateSystem()
    ) {
      return;
    }

    // Very hacky way to ensure that the camera is not missplaced when
    // focused according to the shared view transformation
    // TODO: Refactor the camera positioning and transformation
    // TODO: Not enough time cuz deployment, but this will have to be cleaned up
    if (this.blockFirstPositionalCorrection) {
      this.blockFirstPositionalCorrection = false;
      return;
    }

    const projectionPoint = CoordinateManager.getInstance().convertToProject(
      this.wgs84Coords.x,
      this.wgs84Coords.y,
      'wgs84',
    );
    const newLocalPos = CoordinateManager.getInstance().projectToLocal(
      projectionPoint.x,
      projectionPoint.y,
    );

    this.activeCamera.setPosition(newLocalPos);
  }

  /**
   * @description Updated the camera geographical coordinates.
   * instance to be able to transition between the different scenes looking approximately at the same position.
   */
  updateGeographicCoords() {
    if (!this.activeCamera) {
      return;
    }

    // instance will stop the camera transitions between projection and orthocamera.
    // But as projection is needed to upload the ortho photo, instance would never haoppend.
    if (!CoordinateManager.getInstance().hasCoordinateSystem()) {
      return;
    }

    const point = this.activeCamera.getLookAtPosition();
    const controller = this.getActiveController();

    /**
     * Refreshes the focus point before switching scene.
     * This ensures the focus point is correct when calculating the new cameras position.
     */
    if (controller instanceof OrbitCameraController) {
      controller.refreshFocusPoint();
    }

    const projectionPoint = CoordinateManager.getInstance().localToProject(point.x, point.y);
    this.wgs84Coords = CoordinateManager.getInstance().convertFromProject(
      projectionPoint.x,
      projectionPoint.y,
      'wgs84',
    );
  }

  focusOnPoint(latitude: number, longitude: number) {
    const projection = CoordinateManager.getInstance().getProjectProjection();
    const swerefCoords = CoordinateManager.getInstance().convertCoords(
      longitude,
      latitude,
      'wgs84',
      projection,
    );
    const localCoords = CoordinateManager.getInstance().projectToLocal(
      swerefCoords.x,
      swerefCoords.y,
    );

    if ((this.getActiveCamera() as any).tweenToPosition) {
      (this.getActiveCamera() as any).tweenToPosition(localCoords);
    }
  }

  /**
   * @description Returns the last wgs84 coordinates that was registered
   */
  getWGS84Coord(): THREE.Vector2 | undefined {
    return this.wgs84Coords;
  }

  /**
   * @description Clears the wgs84 coordinates
   */
  clearWGS84Coord() {
    this.wgs84Coords = undefined;
  }

  /**
   * @param guid
   * @param copyViewFromCamera
   *  @description Sets the active camera based on the guid
   */
  setActiveCamera(guid: string, skipOrigoTransformation: boolean) {
    if (!guid) {
      return;
    }

    const oldActiveCamera = this.activeCamera;
    this.activeCamera = this.cameras[guid];

    // Reactivating the same camera (e.g. timeline switch within the same scene
    // type) must preserve zoom and position. Geographical alignment across a
    // projection change is handled separately via updateCameraLocalPosition
    // (triggered by projectionUpdatedEvent), so a self-copy here would only
    // reset zoom and double-apply the origo offset.
    const isSameCamera = oldActiveCamera === this.activeCamera;

    if (oldActiveCamera && !isSameCamera) {
      this.activeCamera.copyViewFromCamera(oldActiveCamera, skipOrigoTransformation);
    } else if (!oldActiveCamera && this.canvasHeight > 0) {
      // Ensure the new active camera aspect ratio matches existing canvas
      this.activeCamera.updateAspect(this.canvasWidth, this.canvasHeight);
    }

    publish(SubscriptionTopic.OnCameraChanged, {
      camera: this.activeCamera,
    });

    return this.activeCamera;
  }

  setActiveCameraAndController(type: CameraType) {
    const camera = this.getCameraByType(type);

    if (!camera) {
      throw new Error(`No camera was found for camera mode "${type}"`);
    }

    this.setActiveCamera(camera.uuid, true);

    switch (camera.cameraType) {
      case CameraType.PerspectiveCamera:
        this.setOrbitController();
        break;
      case CameraType.OrthographicCamera:
        this.setOrthotileController();
        break;
    }
  }

  /**
   *  @description Returns the current active THREE.Camera object.
   */
  getActiveCamera(): SkyviewCamera | undefined {
    return this.activeCamera;
  }

  /**
   * @param guid
   *  @description Returns the THREE.Camera object based on the gUID.
   */
  getCamera(guid: string): SkyviewCamera | undefined {
    return this.cameras[guid];
  }

  /**
   *  Set the cameras that are currently available to switch between
   * @param cameraUuids
   */
  setAvailableCameras(cameraUuids: string[]) {
    const allAvailableCamerasExist = cameraUuids.reduce(
      (prev, cameraUuid) => prev && !!Object.keys(this.cameras).find((key) => key === cameraUuid),
      true,
    );

    if (!allAvailableCamerasExist) {
      throw new Error('Not all cameras that is set to available are registered cameras.');
    }

    this.availableCameras = cameraUuids;
  }

  /**
   *  Return all of the current available camera uuids
   */
  getAvailableCameraUuids(): string[] {
    return this.availableCameras;
  }

  /**
   *  Return all of the currently available cameras
   */
  getAvailableCameras(): SkyviewCamera[] {
    return this.availableCameras.map((uuid) => this.cameras[uuid]);
  }

  /**
   * @param width
   * @param height
   * @param guid
   * @description Updates hte aspect ratio for the camera matching the guid
   * If guid is undefined, update ther active camera,
   */
  setAspectRatio(width: number, height: number, guid?: string) {
    const camera = guid ? this.cameras[guid] : this.getActiveCamera();

    if (width <= 0 || height <= 0) {
      return;
    }

    this.canvasWidth = width;
    this.canvasHeight = height;

    if (!camera) {
      return;
    }

    camera.updateAspect(width, height);
  }

  /**
   *  @description Returns the canvas width
   */
  getCanvasWidth() {
    return this.canvasWidth;
  }

  /**
   *  @description Returns the canvas height
   */
  getCanvasHeight() {
    return this.canvasHeight;
  }

  /**
   * @param uuid
   * @description Remove camera
   */
  destroyCamera(uuid: string) {
    delete this.cameras[uuid];
  }

  /**
   *  Set orbit controller
   * @param camera
   */
  setOrbitController(camera?: SkyviewCamera) {
    this.setPerspectiveController('orbit', camera);
  }

  /**
   * Sets the perspective camera's controller for how the user controls it.
   * @param control
   * @param camera
   */
  setPerspectiveController(control: CameraControl, camera?: SkyviewCamera) {
    camera = camera || this.getActiveCamera();
    if (!camera || camera.cameraType !== CameraType.PerspectiveCamera) {
      console.log('There is not camera or the camera types does not match the controller');
      return;
    }

    this.activeCamera = camera;

    this.replaceController(
      new perspectiveControllers[control](this.getActiveCamera() as SkyviewPerspectiveCamera),
      control,
    );
  }

  /**
   *  How the perspective camera is controlled, or undefined when another camera is active.
   */
  getCameraControl(): CameraControl | undefined {
    return this.cameraControl;
  }

  /**
   *  Set orbit controller
   * @param camera
   */
  setFPSController(camera?: SkyviewCamera) {
    camera = camera || this.getActiveCamera();
    if (!camera || camera.cameraType !== CameraType.FPSCamera) {
      console.log('There is not camera or the camera types does not match the controller');
      return;
    }

    this.activeCamera = camera;

    this.replaceController(new FpsCameraController(this.getActiveCamera() as SkyviewFpsCamera));
  }

  /**
   *  Set orthotile controller
   * @param camera
   */
  setOrthotileController(camera?: SkyviewCamera) {
    camera = camera || this.getActiveCamera();
    if (!camera || camera.cameraType !== CameraType.OrthographicCamera) {
      console.log('There is not camera or the camera types does not match the controller');
      return;
    }

    this.activeCamera = camera;

    this.replaceController(
      new OrthotileCameraController(this.getActiveCamera() as SkyviewOrthographicCamera),
    );
  }

  /**
   *  Get the active camera controller
   */
  getActiveController(): CameraControllerInterface | undefined {
    return this.activeController;
  }

  private replaceController(controller: CameraControllerInterface, control?: CameraControl) {
    this.activeController?.dispose?.();
    this.activeController = controller;
    this.cameraControl = control;
  }

  private getCameraByType(type: CameraType): SkyviewCamera | undefined {
    return this.getAvailableCameras().find((camera) => camera.cameraType === type);
  }
}

let instance: CameraManager;
/**
 *
 */
export default function () {
  instance = instance || new CameraManager();
  return instance;
}
